import React from 'react';
import { ProjectCard } from '../molecules/ProjectCard';
import { SkeletonCard } from '../molecules/skeleton-card';
import { ProjectsListProps } from '../../types/ProjectsList.types';
import UpgradeCtaCard from '../molecules/UpgradeCtaCard';

/**
 * ProjectsList organism component
 *
 * Displays a grid of project cards with loading states and a "New Project" button.
 * Platform-agnostic design with customizable columns, gap, and event handlers.
 *
 * **Features:**
 * - Grid layout with 1-4 columns
 * - Loading state with skeleton cards
 * - "New Project" button card (always first)
 * - Individual project cards with delete menu
 * - Responsive grid with customizable gap
 * - Full i18n support via props
 *
 * **States:**
 * 1. Loading: Shows skeleton cards
 * 2. Empty: Shows only "New Project" button
 * 3. With Projects: Shows "New Project" + project cards
 *
 * @example
 * ```tsx
 * // Basic usage
 * <ProjectsList
 *   projects={projects}
 *   isLoading={false}
 *   onProjectClick={(id) => navigate(`/project/${id}`)}
 *   onProjectDelete={(id, name) => confirmDelete(id, name)}
 *   onNewProjectClick={() => createNewProject()}
 * />
 *
 * // With i18n
 * <ProjectsList
 *   projects={projects}
 *   title={t('my_projects')}
 *   newProjectText={t('new_project')}
 *   columns={3}
 *   gap="md"
 * />
 * ```
 */


export const ProjectsList: React.FC<ProjectsListProps> = (props) => {
  const {
    projects = [],
    isLoading = false,
    onProjectClick,
    onProjectDelete,
    onNewProjectClick,
    title = 'My Projects',
    newProjectText = 'New Project',
    skeletonCount = 3,
    columns = 3,
    gap = 'md',
    className = '',
    testId = 'projects-list',
    showRecent = false,
    planDetails,
    upgradeLink,
    disabled = false,
    hideNewProjectCard = false,
    isWeb
  } = props;
  // Filter and sort projects if showRecent is enabled
  const displayProjects = React.useMemo(() => {


    // Sort by updatedAt (most recent first) and take only 2
    const sortedProjects = [...projects]
      .sort((a, b) => {
        const dateA = a.contentUpdatedAt ? new Date(a.contentUpdatedAt).getTime() : 0;
        const dateB = b.contentUpdatedAt ? new Date(b.contentUpdatedAt).getTime() : 0;
        return dateB - dateA;
      })
      if (showRecent) { 
        return sortedProjects.slice(0, 2);
      }
      return sortedProjects;
  }, [projects, showRecent]);

  // Use "Recent projects" title when showRecent is true
  const displayTitle = showRecent && title === 'My Projects' ? 'Recent projects' : title;

  // Grid column classes
  const columnClasses = {
    1: 'grid-cols-1',
    2: 'grid-cols-1 lg:grid-cols-2',
    3: 'grid-cols-1 lg:grid-cols-3',
    4: 'grid-cols-1 lg:grid-cols-4',
  };

  // Gap classes
  const gapClasses = {
    sm: 'gap-4',
    md: 'gap-6',
    lg: 'gap-8',
  };

  // Show Upgrade CTA card if plan is 'free' and at or above project limit
  const isFreePlan = planDetails?.name?.toLowerCase() === 'free';
  const projectLimit = planDetails?.features?.find((f: any) => f.name === 'Projects')?.maxLimit;
  const showUpgradeCard = isFreePlan && projects.length >= projectLimit;

  return (
    <div 
      className={`flex flex-col gap-4 ${className} ${disabled ? 'opacity-60' : ''}`} 
      data-testid={testId}
    >
      <ul
               className={`grid ${ isWeb ? columnClasses[columns] : 'grid-cols-3'} ${gapClasses[gap]}`}
        data-testid={`${testId}-grid`}
      >
        {isLoading ? (
          // Loading state: Skeleton cards
          Array.from({ length: skeletonCount }).map((_, index) => (
            <li key={`skeleton-${index}`}>
              <SkeletonCard delay={index} />
            </li>
          ))
        ) : (
          <>
            {/* New Project Button Card (always first, hidden if limit reached or disabled/archived mode) */}
            {!showUpgradeCard && !hideNewProjectCard && !disabled && (
              <li>
                <ProjectCard
                  newProject
                  newProjectText={newProjectText}
                  onClick={onNewProjectClick}
                  testId={`${testId}-new-project`}
                />
              </li>
            )}

            {/* Project Cards */}
            {displayProjects.map((project) => (
              <li 
                key={project.id} 
                className={disabled ? 'cursor-not-allowed pointer-events-none' : ''}
              >
                <ProjectCard
                  project={project}
                  onClick={disabled ? undefined : () => onProjectClick?.(project.id)}
                  onDelete={
                    disabled || !onProjectDelete 
                      ? undefined 
                      : () => onProjectDelete(project.id, project.name)
                  }
                  testId={`${testId}-project`}
                />
              </li>
            ))}

            {/* Upgrade CTA Card (free plan limit reached, not shown in disabled mode) */}
            {showUpgradeCard && !disabled && (
              <li>
                <UpgradeCtaCard
                  title="Upgrade to create more"
                  description="Unlock unlimited projects and advanced features"
                  buttonText="Upgrade"
                  onUpgrade={upgradeLink}
                />
              </li>
            )}
          </>
        )}
      </ul>
    </div>
  );
};

ProjectsList.displayName = 'ProjectsList';
